import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Divider, Typography } from "@mui/material";
import { Box, Button, Checkbox, FlexColBox, Link } from "../common/components";
import Dialog from "../common/components/Dialog/Dialog";
import { useTeam } from "../context/Team/useTeam";
import { useSelector } from "../common/hooks/useSelector";
import TeamService from "../service/TeamService";
import { palette } from "../styles/theme";

interface Props {
  open: boolean;
  onClose: () => void;
}

const RageQuitModal = ({ open, onClose }: Props) => {
  const { t } = useTranslation();
  const { teams, refetchTeams } = useTeam();
  const userInfos = useSelector((state) => state.userInfo);
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const [isLeaving, setIsLeaving] = useState(false);

  const userTeams = useMemo(() => {
    return teams.filter((team) =>
      team.teammates.some((teammate) => teammate.id === userInfos.id)
    );
  }, [teams, userInfos.id]);

  const handleToggleTeam = (teamId: string) => {
    setSelectedTeamIds((prev) =>
      prev.includes(teamId)
        ? prev.filter((id) => id !== teamId)
        : [...prev, teamId]
    );
  };

  const handleSelectAll = () => {
    setSelectedTeamIds(userTeams.map((team) => team.id));
  };

  const handleClear = () => {
    setSelectedTeamIds([]);
  };

  const handleLeaveTeams = useCallback(async () => {
    if (selectedTeamIds.length === 0) return;

    setIsLeaving(true);
    try {
      await TeamService.rageQuit(selectedTeamIds);

      toast.success(t("rage_quit.success", { count: selectedTeamIds.length }));
      await refetchTeams({ skipLoading: true });
      setSelectedTeamIds([]);
      onClose();
    } catch (error) {
      console.error("Failed to leave teams:", error);
      toast.error(t("rage_quit.error"));
    } finally {
      setIsLeaving(false);
    }
  }, [selectedTeamIds, t, refetchTeams, onClose]);

  const content = (
    <FlexColBox gap="16px">
      {userTeams.length === 0 ? (
        <Typography variant="textSm" color={palette.grey.primary}>
          {t("rage_quit.no_teams")}
        </Typography>
      ) : (
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Link
              onClick={(e) => {
                e.preventDefault();
                handleSelectAll();
              }}
              style={{ fontSize: "14px", color: palette.blue.secondary }}
            >
              {t("general.select_all")}
            </Link>
            <Link
              onClick={(e) => {
                e.preventDefault();
                handleClear();
              }}
              style={{ fontSize: "14px", color: palette.blue.secondary }}
            >
              {t("general.clear")}
            </Link>
          </Box>

          <Divider />

          <FlexColBox
            gap="8px"
            sx={{
              maxHeight: "300px",
              overflowY: "auto",
            }}
          >
            {userTeams.map((team) => (
              <Checkbox
                key={team.id}
                checked={selectedTeamIds.includes(team.id)}
                onChange={() => handleToggleTeam(team.id)}
                label={team.name}
                labelVariant="textSm"
              />
            ))}
          </FlexColBox>
        </>
      )}
    </FlexColBox>
  );

  const action = (
    <Button
      fullWidth
      onClick={handleLeaveTeams}
      disabled={selectedTeamIds.length === 0 || isLeaving}
      isLoading={isLeaving}
    >
      {t("rage_quit.leave_teams", { count: selectedTeamIds.length })}
    </Button>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("rage_quit.title")}
      content={content}
      action={action}
    />
  );
};

export default RageQuitModal;
